import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  environmentLabel,
  peerStatusMeta,
  releaseColour,
  severityColour,
} from './flagDisplay';

const chipStyle = ({ bg, fg }) => ({
  backgroundColor: bg,
  color: fg,
  fontWeight: 500,
  height: 22,
});

/**
 * The rows one environment would create, or did.
 *
 * `releaseStatus` is rendered per row because it legitimately differs per environment — ALL
 * in development and staging, OFF in production — and that difference is the clearest way to
 * show that creating a flag is not releasing it.
 */
const CreatedRows = ({ created }) => (
  <Box mt={1}>
    {created.map((row) => (
      <Box
        key={row.key}
        display="flex"
        alignItems="center"
        style={{ gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
          {row.key}
        </Typography>
        {row.kind === 'CONTAINER' && (
          <Tooltip title="Namespace node derived from the key. Created automatically, and a lever that takes everything beneath it down at once.">
            <Chip size="small" variant="outlined" label="container" style={{ height: 22 }} />
          </Tooltip>
        )}
        <Chip
          size="small"
          label={row.releaseStatus}
          style={chipStyle(releaseColour(row.releaseStatus))}
        />
      </Box>
    ))}
  </Box>
);

/**
 * One environment's slice of the response — the one you called, or a peer.
 *
 * A peer that failed carries no result, only a `detail`, so the two cases are rendered from
 * different fields rather than pretending a failure produced an empty plan.
 */
const EnvironmentBlock = ({ environment, status, result, detail }) => {
  const meta = status ? peerStatusMeta(status) : null;
  const created = (result && result.created) || [];
  const alreadyPresent = (result && result.alreadyPresent) || [];
  const orphans = (result && result.orphans) || [];
  const dryRun = result ? result.dryRun : undefined;

  return (
    <Box
      mb={1}
      p={1.5}
      border="1px solid #eceff1"
      borderRadius={4}
      style={{ backgroundColor: status === 'FAILED' ? '#fff8f7' : '#fff' }}>
      <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          {environmentLabel(environment)}
        </Typography>
        {meta && (
          <Tooltip title={meta.note}>
            <Chip size="small" label={meta.label} style={chipStyle(severityColour(meta.severity))} />
          </Tooltip>
        )}
        {dryRun === true && (
          <Chip
            size="small"
            label="nothing written"
            style={chipStyle(severityColour('info'))}
          />
        )}
      </Box>

      {detail && (
        <Typography variant="caption" style={{ color: '#b3261e', display: 'block', marginTop: 4 }}>
          {detail}
        </Typography>
      )}

      {status === 'FAILED' && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
          {peerStatusMeta('FAILED').note}
        </Typography>
      )}

      {status === 'SKIPPED' && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block' }}>
          {peerStatusMeta('SKIPPED').note}
        </Typography>
      )}

      {result && (
        <>
          {created.length > 0 ? (
            <>
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
                {dryRun ? 'Would create' : 'Created'} {created.length} row(s)
              </Typography>
              <CreatedRows created={created} />
            </>
          ) : (
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 6 }}>
              Nothing to create — every declared flag already has a row here.
            </Typography>
          )}

          {alreadyPresent.length > 0 && (
            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
              {alreadyPresent.length} already present
            </Typography>
          )}

          {orphans.length > 0 && (
            <Box mt={1}>
              {/* Hygiene, not an error, and deliberately no delete button: removing a flag
                  is a code change first and a row deletion second. Nothing here ever deletes. */}
              <Alert severity="warning">
                <Typography variant="body2">
                  {orphans.length} row(s) the catalog no longer declares. Nothing was deleted —
                  removing a flag is a backend PR first, then a row deletion.
                </Typography>
                <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                  {orphans.join(', ')}
                </Typography>
              </Alert>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * Renders the response shared by `POST feature-flags` and `POST feature-flags/sync`.
 *
 * A failed peer does not fail the request: the local write has already committed, so the
 * HTTP status is 200 with a FAILED entry inside it. Anything that only checked the status
 * code would ship a silent production divergence, which is why the banner below reads
 * `peers[].status` rather than trusting the call's success.
 */
const SyncResultView = ({ result }) => {
  if (!result) return null;

  const local = result.local || {};
  const peers = result.peers || [];
  const failedPeers = peers.filter((peer) => peer.status === 'FAILED');

  return (
    <Box>
      {failedPeers.length > 0 && (
        <Box mb={2}>
          <Alert severity="warning">
            <strong>
              {failedPeers.length} environment(s) did not apply:{' '}
              {failedPeers.map((peer) => environmentLabel(peer.environment)).join(', ')}
            </strong>
            . The write here succeeded, so the environments have diverged. Re-run the sync —
            it is idempotent.
          </Alert>
        </Box>
      )}

      <Typography variant="caption" color="textSecondary">
        This environment
      </Typography>
      <EnvironmentBlock environment={local.environment} result={local} />

      {peers.length > 0 && (
        <>
          <Typography variant="caption" color="textSecondary">
            Other environments
          </Typography>
          {peers.map((peer) => (
            <EnvironmentBlock
              key={peer.environment}
              environment={peer.environment}
              status={peer.status}
              result={peer.result}
              detail={peer.detail}
            />
          ))}
        </>
      )}

      {peers.length > 0 && (
        <Typography variant="caption" color="textSecondary">
          Release state is never propagated — each environment computes its own, which is why
          production shows OFF. Turning a feature on is a per-environment action, done from the
          release dialog in that environment.
        </Typography>
      )}
    </Box>
  );
};

export default SyncResultView;
